import React, { useState } from 'react';
import VerificationCard from './VerificationCard';
import RejectModal from './RejectModal';
import { useVerificationActions } from '@/hooks/useVerificationActions';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

interface VerificationsListProps {
  verifications: any[] | undefined;
  isLoading: boolean;
  onActionComplete?: () => void;
  type: 'waiting' | 'pending';
}

const VerificationsList: React.FC<VerificationsListProps> = ({
  verifications,
  isLoading,
  onActionComplete,
  type,
}) => {
  const [selectedVerificationId, setSelectedVerificationId] = useState<string | null>(null);
  const [selectedUsername, setSelectedUsername] = useState<string>('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  
  const { approveVerification, rejectVerification } = useVerificationActions();

  const handleApprove = async (verificationId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    approveVerification.mutate(
      { verificationId, adminId: user.id },
      {
        onSuccess: () => {
          onActionComplete?.();
        },
      }
    );
  };

  const handleRejectClick = (verificationId: string, username: string) => {
    setSelectedVerificationId(verificationId);
    setSelectedUsername(username);
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async (reason: string) => {
    if (!selectedVerificationId) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    rejectVerification.mutate(
      {
        verificationId: selectedVerificationId,
        adminId: user.id,
        reason,
      },
      {
        onSuccess: () => {
          setShowRejectModal(false);
          setSelectedVerificationId(null);
          setSelectedUsername('');
          onActionComplete?.();
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!verifications || verifications.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">
          Nenhuma verificação {type === 'waiting' ? 'aguardando revisão' : 'em andamento'}.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {verifications.map((verification) => (
          <VerificationCard
            key={verification.id}
            verification={verification}
            type={type}
            onApprove={handleApprove}
            onReject={(id) => handleRejectClick(id, verification.username)}
            isLoading={approveVerification.isPending || rejectVerification.isPending}
          />
        ))}
      </div>

      <RejectModal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setSelectedVerificationId(null);
          setSelectedUsername('');
        }}
        onConfirm={handleRejectConfirm}
        username={selectedUsername}
        isLoading={rejectVerification.isPending}
      />
    </>
  );
};

export default VerificationsList;
